import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { HardDrive, Loader2, CheckCircle2, AlertTriangle, RotateCcw, Link2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Storage-Werkzeuge (Admin):
 *  1. Einmalige Migration R2 -> Railway-Bucket
 *  2. Basis-URL umschreiben - nötig nach einem Domain-Wechsel, weil die
 *     gespeicherten Datei-URLs die Domain enthalten (…/api/storage/…).
 */
export default function StorageMigrationSection({ api }) {
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [fromBase, setFromBase] = useState('');
    const [toBase, setToBase] = useState('');

    const run = async ({ execute = false, reverse = false, explicit = false } = {}) => {
        if (explicit && !fromBase.trim()) {
            toast.error('Bitte die alte Basis-URL angeben');
            return;
        }
        setLoading(true);
        setResult(null);
        try {
            const params = new URLSearchParams();
            if (execute) params.set('confirm', 'JA-UMSCHREIBEN');
            if (reverse) params.set('reverse', 'true');
            if (explicit) {
                params.set('from_base', fromBase.trim());
                if (toBase.trim()) params.set('to_base', toBase.trim());
            }
            const res = await api.post(`/storage/migrate-urls?${params.toString()}`);
            setResult(res.data);
            if (execute) toast.success(`${res.data.total_documents} Dokumente umgeschrieben`);
            else toast.info(`Test: ${res.data.total_documents} Dokumente betroffen`);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Migration fehlgeschlagen');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card>
            <CardHeader>
                <div className="flex items-center gap-2">
                    <HardDrive className="w-5 h-5 text-primary" />
                    <CardTitle>Datei-Speicher</CardTitle>
                </div>
                <CardDescription>
                    Hochgeladene Dateien werden über <code className="font-mono text-xs">…/api/storage/…</code> ausgeliefert —
                    die gespeicherten URLs enthalten also die <strong>Domain</strong>, nicht den Bucket. Solange die Domain
                    gleich bleibt, überstehen die URLs auch einen Server- oder Bucket-Wechsel.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">

                {/* --- 1. Domain-Wechsel --- */}
                <div className="space-y-3">
                    <div className="flex items-center gap-2">
                        <Link2 className="w-4 h-4 text-primary" />
                        <h3 className="font-medium text-sm">Basis-URL umschreiben (nach Domain-Wechsel)</h3>
                    </div>
                    <p className="text-xs text-muted-foreground">
                        Nach einem Domain-Wechsel zeigen die gespeicherten Datei-URLs noch auf die alte Adresse.
                        Hier einmal die alte Basis eintragen — leer gelassenes Ziel heißt „aktuelle Adresse“.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs">Alte Basis-URL</Label>
                            <Input
                                value={fromBase}
                                onChange={(e) => setFromBase(e.target.value)}
                                placeholder="https://alte-domain.at/api/storage"
                                className="rounded-xl mt-1.5 font-mono text-xs"
                            />
                        </div>
                        <div>
                            <Label className="text-xs">Neue Basis-URL (optional)</Label>
                            <Input
                                value={toBase}
                                onChange={(e) => setToBase(e.target.value)}
                                placeholder="leer = aktuelle Adresse"
                                className="rounded-xl mt-1.5 font-mono text-xs"
                            />
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button variant="outline" size="sm" className="rounded-xl gap-2"
                            onClick={() => run({ explicit: true })} disabled={loading}>
                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                            Test
                        </Button>
                        <Button size="sm" className="rounded-xl gap-2" disabled={loading}
                            onClick={() => {
                                if (window.confirm('Alle Datei-URLs von der alten auf die neue Basis umschreiben?')) {
                                    run({ explicit: true, execute: true });
                                }
                            }}>
                            <Link2 className="w-4 h-4" /> Umschreiben
                        </Button>
                    </div>
                </div>

                <div className="border-t border-border" />

                {/* --- 2. Einmalige R2 -> Bucket-Migration --- */}
                <div className="space-y-3">
                    <h3 className="font-medium text-sm">Einmalige Migration: Cloudflare R2 → Railway-Bucket</h3>
                    <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-400 text-xs flex gap-2">
                        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span>
                            Nur nötig, solange noch R2-URLs in der Datenbank stehen. Voraussetzung:
                            <code className="font-mono mx-1">STORAGE_PROVIDER=bucket</code> und die Dateien sind bereits kopiert.
                            Umkehrbar.
                        </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button variant="outline" size="sm" className="rounded-xl gap-2"
                            onClick={() => run({})} disabled={loading}>
                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                            Test (Dry-Run)
                        </Button>
                        <Button size="sm" className="rounded-xl gap-2" disabled={loading}
                            onClick={() => {
                                if (window.confirm('Alle R2-URLs jetzt auf den Railway-Bucket umschreiben?')) {
                                    run({ execute: true });
                                }
                            }}>
                            <HardDrive className="w-4 h-4" /> Jetzt migrieren
                        </Button>
                        <Button variant="ghost" size="sm" className="rounded-xl gap-2 text-muted-foreground" disabled={loading}
                            onClick={() => {
                                if (window.confirm('Migration rückgängig machen (zurück zu R2)?')) {
                                    run({ execute: true, reverse: true });
                                }
                            }}>
                            <RotateCcw className="w-4 h-4" /> Zurückdrehen
                        </Button>
                    </div>
                </div>

                {result && (
                    <div className="p-4 rounded-xl bg-muted/40 border border-border text-sm space-y-2">
                        <div className="flex items-center gap-2 font-medium">
                            {result.executed ? (
                                <><CheckCircle2 className="w-4 h-4 text-green-500" /> Ausgeführt{result.reverse ? ' (zurückgedreht)' : ''}</>
                            ) : (
                                <><CheckCircle2 className="w-4 h-4 text-blue-500" /> Test abgeschlossen (nichts geändert)</>
                            )}
                        </div>
                        <p className="text-muted-foreground">
                            {result.total_documents} Dokumente {result.executed ? 'umgeschrieben' : 'betroffen'}
                        </p>
                        <p className="text-xs text-muted-foreground font-mono break-all">
                            {result.from_base} → {result.to_base}
                        </p>
                        {result.per_collection && Object.keys(result.per_collection).length > 0 && (
                            <div className="text-xs text-muted-foreground pt-1">
                                {Object.entries(result.per_collection).map(([k, v]) => (
                                    <span key={k} className="inline-block mr-3">{k}: {v}</span>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
